// =====================================================
// src/pages/Projectbeheer.js
// vPRJ-030
//
// Projectbeheer:
// - Beheer van projectonderdelen / bouwstenen
// - Onbeperkte lagen via parent_id
// - Nummering via code / full_code
// - Volledige naam via full_name
// - Midden grid met zoeken, filters en sortering
// - Rechter invoerkolom alleen actief na Nieuw of selectie
// - Popup voor offerte- en interne projectteksten
// - Spreekbasis aanwezig
// =====================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const VERSION = "vPRJ-030";

const emptyForm = {
  id: null,
  copy_from_id: "",
  copy_children: false,
  parent_id: "",
  code: "",
  name: "",
  full_code: "",
  full_name: "",
  level: 1,
  sort_order: 0,
  offer_text: "",
  internal_text: "",
  line_type: "item",
  price: "",
  vat_rate: "hoog",
  is_active: true,
};

function euro(v) {
  const n = Number(v || 0);
  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
  });
}

function norm(v) {
  return String(v || "").toLowerCase().trim();
}
function lineTypeLabel(value) {
  const t = String(value || "item").toLowerCase();

  if (t === "text") return "Tekst";
  if (t === "heading") return "Kop";
  if (t === "subtotal") return "Subtotaal";
  if (t === "total") return "Totaal";

  return "Artikel";
}

function isAmountLineType(value) {
  const t = String(value || "item").toLowerCase();
  return t === "item";
}


async function api(url, options = {}) {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token");

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${txt ? " - " + txt : ""}`);
  }

  return res.json();
}

export default function Projectbeheer() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedId, setSelectedId] = useState(null);

  const [zoek, setZoek] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterNaam, setFilterNaam] = useState("");
  const [filterParent, setFilterParent] = useState("");
  const [filterLaag, setFilterLaag] = useState("");
  const [filterActief, setFilterActief] = useState("");

const [sort, setSort] = useState({ key: "full_code", dir: "asc" });
  const [mode, setMode] = useState("search");

const [loading, setLoading] = useState(false);
  const [melding, setMelding] = useState("");
  const [fout, setFout] = useState("");

  const [speechInfo, setSpeechInfo] = useState("");
  const [hideVoiceError, setHideVoiceError] = useState(false);
  const prevVoiceErrorRef = useRef("");

const [tekstPopupOpen, setTekstPopupOpen] = useState(false);

const pageRef = useRef(null);
  const rightPanelRef = useRef(null);

  const zoekRef = useRef(null);
  const naamRef = useRef(null);
  const codeRef = useRef(null);
  const prijsRef = useRef(null);
  const offerteTekstRef = useRef(null);
  const interneTekstRef = useRef(null);

  const popupOfferteTekstRef = useRef(null);
  const popupInterneTekstRef = useRef(null);

  // Onthoudt welk veld het laatst actief was.
  // Dit is nodig omdat de knop Spreek in het midden staat,
  // terwijl je vaak rechts wilt dicteren.
  const lastActiveFieldRef = useRef("zoek");

  const [tekstDraft, setTekstDraft] = useState({
    offer_text: "",
    internal_text: "",
  });

const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState("");

  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiSource, setAiSource] = useState("page");
  const aiTextRef = useRef(null);

  const rawUser = sessionStorage.getItem("user") || localStorage.getItem("user") || "";
  let role = "user";
  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

const isReadonly = String(role).toLowerCase() === "readonly";
  const isFormLocked = mode !== "form";
  const isFormDisabled = isReadonly || isFormLocked;

const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6500,

    onCommand: (cmd, rawHeard) => {
      const raw = String(rawHeard || "").trim();

      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      if (raw && handleVoiceCommand(raw)) {
        return true;
      }

      if (!cmd || !cmd.type) {
        return false;
      }

      if (cmd.type === "new" || cmd.type === "nieuw") {
        nieuw();
        return true;
      }

      if (cmd.type === "refresh") {
        vernieuw();
        return true;
      }

      if (cmd.type === "save") {
        opslaan();
        return true;
      }

      if (cmd.type === "delete") {
        verwijderen();
        return true;
      }

      return false;
    },

    onDictation: (text) => {
      const raw = String(text || "").trim();

      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      if (handleVoiceCommand(raw)) {
        return;
      }

      applyDictationToProjectField(raw);
    },
  });

  useEffect(() => {
    const cur = String(voice.error || "");
    const prev = String(prevVoiceErrorRef.current || "");

    if (cur && cur !== prev) {
      setHideVoiceError(false);
      prevVoiceErrorRef.current = cur;
    }

    if (!cur) {
      prevVoiceErrorRef.current = "";
    }
  }, [voice.error]);

function detectActiveProjectField() {
    const el = document.activeElement;

    if (el === zoekRef.current) {
      lastActiveFieldRef.current = "zoek";
      return;
    }

    if (el === codeRef.current) {
      lastActiveFieldRef.current = "code";
      return;
    }

    if (el === naamRef.current) {
      lastActiveFieldRef.current = "naam";
      return;
    }

    if (el === prijsRef.current) {
      lastActiveFieldRef.current = "prijs";
      return;
    }

    if (el === offerteTekstRef.current) {
      lastActiveFieldRef.current = "offer_text";
      return;
    }

    if (el === interneTekstRef.current) {
      lastActiveFieldRef.current = "internal_text";
      return;
    }

    if (el === popupOfferteTekstRef.current) {
      lastActiveFieldRef.current = "popup_offer_text";
      return;
    }

    if (el === popupInterneTekstRef.current) {
      lastActiveFieldRef.current = "popup_internal_text";
      return;
    }
  }

function maakZinnenNetjes(text) {
    let txt = String(text || "");

    txt = txt
      .replace(/\s+([.,!?;:])/g, "$1")
      .replace(/([.,!?;:])(?=\S)/g, "$1 ");

    txt = txt.replace(/(^|[.!?]\s+|\n+)([a-zà-ÿ])/g, (match, prefix, letter) => {
      return prefix + String(letter || "").toUpperCase();
    });

    return txt;
  }

  function appendText(current, raw) {
    const cur = String(current || "");
    const txt = nlText(raw);

    if (!txt) return cur;

    const isPunctuationOnly = /^[.,!?;:]$/.test(txt);

    let out = "";

    if (isPunctuationOnly) {
      out = `${cur}${txt}`;
    } else {
      const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
      out = `${cur}${sep}${txt}`;
    }

    return maakZinnenNetjes(out).trim();
  }

  function applyDictationToProjectField(raw) {
    const target = lastActiveFieldRef.current || "zoek";

    if (tekstPopupOpen) {
      if (target === "popup_offer_text") {
        setTekstDraft((p) => ({
          ...p,
          offer_text: appendText(p.offer_text, raw),
        }));
        return true;
      }

      if (target === "popup_internal_text") {
        setTekstDraft((p) => ({
          ...p,
          internal_text: appendText(p.internal_text, raw),
        }));
        return true;
      }
    }

    if (target === "zoek") {
      setZoek(raw);
      setMode("search");
      return true;
    }

    if (isFormDisabled) {
      return false;
    }

    if (target === "code") {
      setForm((p) => ({
        ...p,
        code: raw.replace(/\s+/g, ""),
      }));
      return true;
    }

    if (target === "naam") {
      setForm((p) => ({
        ...p,
        name: nlText(raw),
      }));
      return true;
    }

    if (target === "prijs") {
      setForm((p) => ({
        ...p,
        price: raw.replace(",", ".").replace(/[^\d.]/g, ""),
      }));
      return true;
    }

    if (target === "offer_text") {
      setForm((p) => ({
        ...p,
        offer_text: appendText(p.offer_text, raw),
      }));
      return true;
    }

    if (target === "internal_text") {
      setForm((p) => ({
        ...p,
        internal_text: appendText(p.internal_text, raw),
      }));
      return true;
    }

    setZoek(raw);
    setMode("search");
    return true;
  }

  function startSpeak() {
    if (!voice.supported || voice.listening) return;

    detectActiveProjectField();

    setHideVoiceError(true);
    voice.startOnce();
  }

  useEffect(() => {
    laad();
  }, []);

async function laad() {
    setLoading(true);
    setFout("");

    try {
      const data = await api("/api/projectmanagement");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setFout(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

function positioneerBovenOpZoek() {
    setTimeout(() => {
      try {
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        zoekRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  function positioneerBovenZonderZoekFocus() {
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch {}

      try {
        if (rightPanelRef.current) {
          rightPanelRef.current.scrollTop = 0;
        }
      } catch {}

      try {
        if (pageRef.current) {
          pageRef.current.scrollIntoView?.({
            behavior: "auto",
            block: "start",
            inline: "nearest",
          });
        }
      } catch {}
    }, 0);
  }

  function positioneerOpRechterKolomMobiel() {
    if (window.innerWidth > 768) return;

    setTimeout(() => {
      try {
        const panel = rightPanelRef.current;
        if (!panel) return;

        panel.scrollTop = 0;

        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  async function vernieuw() {
    setZoek("");
    setFilterCode("");
    setFilterNaam("");
    setFilterParent("");
    setFilterLaag("");
    setFilterActief("");
    setForm(emptyForm);
    setSelectedId(null);
    setMelding("");
    setFout("");
    setMode("search");

    await laad();

    positioneerBovenOpZoek();
  }

  function nieuw() {
    setForm(emptyForm);
    setSelectedId(null);
    setMelding("");
    setFout("");
    setMode("form");
  }

  async function selecteer(item) {
    setSelectedId(item.id);
    setFout("");
    setMelding("");
    setMode("form");

    positioneerOpRechterKolomMobiel();

    try {
      const data = await api(`/api/projectmanagement/${item.id}`);
      setForm({
        ...emptyForm,
        ...data,
        parent_id: data.parent_id ? String(data.parent_id) : "",
        price: data.price ?? "",
        is_active: data.is_active !== false,
      });

      positioneerOpRechterKolomMobiel();
    } catch (e) {
      setFout(e.message || String(e));
    }
  }

async function opslaan() {
    if (isReadonly) return;

    setFout("");
    setMelding("");

    if (!String(form.code || "").trim()) {
      setFout("Nummer/code is verplicht.");
      positioneerBovenOpZoek();
      return;
    }

    if (!String(form.name || "").trim()) {
      setFout("Naam is verplicht.");
      positioneerBovenOpZoek();
      return;
    }

    const isNieuwOnderdeel = !form.id;
    const bronIdVoorKinderen = form.copy_from_id || "";
    const kopieerKinderen = form.copy_children === true;

    const payload = {
      parent_id: form.parent_id ? Number(form.parent_id) : null,
      code: String(form.code || "").trim(),
      name: String(form.name || "").trim(),
      offer_text: form.offer_text || "",
      internal_text: form.internal_text || "",
      line_type: form.line_type || "item",
      price: Number(String(form.price || "0").replace(",", ".")) || 0,
      vat_rate: form.vat_rate || "hoog",
      is_active: form.is_active !== false,
      sort_order: Number(form.sort_order || 0),
    };

    try {
      const saved = form.id
        ? await api(`/api/projectmanagement/${form.id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
          })
        : await api("/api/projectmanagement", {
            method: "POST",
            body: JSON.stringify(payload),
          });

      if (
        isNieuwOnderdeel &&
        kopieerKinderen &&
        bronIdVoorKinderen &&
        saved?.id
      ) {
        await kopieerOnderliggendeOnderdelen({
          bronParentId: bronIdVoorKinderen,
          nieuweParentId: saved.id,
        });
      }

      setForm(emptyForm);
      setSelectedId(null);
      setMode("search");

      if (isNieuwOnderdeel && kopieerKinderen && bronIdVoorKinderen) {
        setMelding("Opgeslagen inclusief onderliggende onderdelen ✅");
      } else {
        setMelding("Opgeslagen ✅");
      }

      await laad();

      positioneerBovenOpZoek();
    } catch (e) {
      setFout(e.message || String(e));
      positioneerBovenOpZoek();
    }
  }

async function verwijderen() {
    if (isReadonly || !form.id) return;

    if (!window.confirm("Weet je zeker dat je dit projectonderdeel wilt verwijderen?")) {
      return;
    }

    try {
      await api(`/api/projectmanagement/${form.id}`, {
        method: "DELETE",
        body: JSON.stringify({}),
      });

      setForm(emptyForm);
      setSelectedId(null);
      setMode("search");
      setMelding("Verwijderd ✅");

      await laad();

      positioneerBovenOpZoek();
    } catch (e) {
      setFout(e.message || String(e));
    }
  }

  function parentNaam(parentId) {
    if (!parentId) return "";
    const p = items.find((x) => String(x.id) === String(parentId));
    if (!p) return "";
    return `${p.full_code || p.code || ""} ${p.name || ""}`.trim();
  }

  const optiesCode = useMemo(
    () => [...new Set(items.map((x) => x.full_code || x.code).filter(Boolean))].sort(),
    [items]
  );

  const optiesNaam = useMemo(
    () => [...new Set(items.map((x) => x.name).filter(Boolean))].sort(),
    [items]
  );

  const optiesParent = useMemo(
    () => [...new Set(items.map((x) => parentNaam(x.parent_id)).filter(Boolean))].sort(),
    [items]
  );

  const optiesLaag = useMemo(
    () => [...new Set(items.map((x) => String(x.level || 1)).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const z = norm(zoek);

    return items.filter((x) => {
      const code = x.full_code || x.code || "";
      const naam = x.name || "";
      const fullName = x.full_name || "";
      const parent = parentNaam(x.parent_id);
      const laag = String(x.level || 1);
      const actief = x.is_active === false ? "Nee" : "Ja";

      const hay = norm(`${code} ${naam} ${fullName} ${parent} ${x.offer_text || ""}`);

      if (z && !hay.includes(z)) return false;
      if (filterCode && code !== filterCode) return false;
      if (filterNaam && naam !== filterNaam) return false;
      if (filterParent && parent !== filterParent) return false;
      if (filterLaag && laag !== filterLaag) return false;
      if (filterActief && actief !== filterActief) return false;

      return true;
    });
  }, [items, zoek, filterCode, filterNaam, filterParent, filterLaag, filterActief]);

const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      let av = "";
      let bv = "";

      if (sort.key === "full_code") {
        av = a.full_code || a.code || "";
        bv = b.full_code || b.code || "";
      }

      if (sort.key === "name") {
        av = a.name || "";
        bv = b.name || "";
      }

      if (sort.key === "parent") {
        av = parentNaam(a.parent_id);
        bv = parentNaam(b.parent_id);
      }

      if (sort.key === "level") {
        return dir * (Number(a.level || 1) - Number(b.level || 1));
      }

      if (sort.key === "price") {
        return dir * (Number(a.price || 0) - Number(b.price || 0));
      }

      if (sort.key === "active") {
        av = a.is_active === false ? "Nee" : "Ja";
        bv = b.is_active === false ? "Nee" : "Ja";
      }

      return dir * String(av).localeCompare(String(bv), "nl");
    });

    return arr;
  }, [filtered, sort]);

  const gridCount = Math.min(sorted.length, 9999);

  function sorteer(key) {
    setSort((p) => ({
      key,
      dir: p.key === key && p.dir === "asc" ? "desc" : "asc",
    }));
  }

function nlText(raw) {
    let txt = String(raw || "").trim();

    if (!txt) return "";

    txt = txt
      .replace(/\bnieuwe regel\b/gi, "\n")
      .replace(/\bnieuwe alinea\b/gi, "\n\n")
      .replace(/\buitroepteken\b/gi, "!")
      .replace(/\bvraagteken\b/gi, "?")
      .replace(/\bdubbele punt\b/gi, ":")
      .replace(/\bpuntkomma\b/gi, ";")
      .replace(/\bpunt\b/gi, ".")
      .replace(/\bkomma\b/gi, ",")
      .replace(/\s+([.,!?;:])/g, "$1")
      .replace(/([.,!?;:])(?=\S)/g, "$1 ");

    return maakZinnenNetjes(txt);
  }

function handleVoiceCommand(raw) {
    const s = norm(raw);

    if (!s) return false;

    if (
      s === "punt" ||
      s === "komma" ||
      s === "vraagteken" ||
      s === "uitroepteken" ||
      s === "dubbele punt" ||
      s === "puntkomma" ||
      s === "nieuwe regel" ||
      s === "nieuwe alinea"
    ) {
      applyDictationToProjectField(s);
      return true;
    }

    if (s === "stop" || s === "klaar") {
      setSpeechInfo("");
      return true;
    }

    if (s === "nieuw" || s === "nieuwe" || s === "nieuw item") {
      nieuw();
      return true;
    }

    if (s === "vernieuw" || s === "vernieuwen" || s === "refresh") {
      vernieuw();
      return true;
    }

    if (s === "opslaan" || s === "bewaar" || s === "bewaren") {
      opslaan();
      return true;
    }

    if (s === "verwijderen" || s === "delete") {
      verwijderen();
      return true;
    }

    if (s.startsWith("zoek ")) {
      setZoek(raw.replace(/^zoek\s+/i, "").trim());
      setMode("search");
      return true;
    }

    return false;
  }

  function sortMark(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

function kopieerVanBron(bronId) {
    if (!bronId) {
      setForm((p) => ({
        ...p,
        copy_from_id: "",
        copy_children: false,
      }));
      return;
    }

    const bron = items.find((x) => String(x.id) === String(bronId));

    if (!bron) return;

    setForm((p) => ({
      ...p,

      copy_from_id: bronId,

      // NIET kopiëren:
      // id
      // parent_id
      // code
      // name
      // full_code
      // full_name
      // level

      offer_text: bron.offer_text || "",
      internal_text: bron.internal_text || "",
      price: bron.price ?? "",
      vat_rate: bron.vat_rate || "hoog",
      is_active: bron.is_active !== false,
    }));
  }

async function kopieerOnderliggendeOnderdelen({ bronParentId, nieuweParentId }) {
    const bronId = String(bronParentId || "");
    const doelParentId = Number(nieuweParentId || 0);

    if (!bronId || !doelParentId) return;

    const kinderen = items
      .filter((x) => String(x.parent_id || "") === bronId)
      .sort((a, b) =>
        String(a.full_code || a.code || "").localeCompare(
          String(b.full_code || b.code || ""),
          "nl"
        )
      );

    for (const kind of kinderen) {
      const payload = {
        parent_id: doelParentId,
        code: String(kind.code || "").trim(),
        name: String(kind.name || "").trim(),
        offer_text: kind.offer_text || "",
        internal_text: kind.internal_text || "",
        line_type: kind.line_type || "item",
        price: Number(String(kind.price || "0").replace(",", ".")) || 0,
        vat_rate: kind.vat_rate || "hoog",
        is_active: kind.is_active !== false,
        sort_order: Number(kind.sort_order || 0),
      };

      const nieuwKind = await api("/api/projectmanagement", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (nieuwKind?.id) {
        await kopieerOnderliggendeOnderdelen({
          bronParentId: kind.id,
          nieuweParentId: nieuwKind.id,
        });
      }
    }
  }

function openTekstPopup() {
    setTekstDraft({
      offer_text: form.offer_text || "",
      internal_text: form.internal_text || "",
    });

    setAiResult("");
    setTekstPopupOpen(true);
  }

  function sluitTekstPopup() {
    setTekstPopupOpen(false);
  }

  function bewaarTekstPopup() {
    setForm((p) => ({
      ...p,
      offer_text: tekstDraft.offer_text || "",
      internal_text: tekstDraft.internal_text || "",
    }));

    setTekstPopupOpen(false);
  }

  function buildProjectContext() {
    const currentId = form.id ? String(form.id) : "";

    const childrenByParent = new Map();

    items.forEach((x) => {
      const pid = x.parent_id ? String(x.parent_id) : "";

      if (!childrenByParent.has(pid)) {
        childrenByParent.set(pid, []);
      }

      childrenByParent.get(pid).push(x);
    });

    function walk(parentId, depth = 0) {
      const rows = childrenByParent.get(String(parentId || "")) || [];

      rows.sort((a, b) =>
        String(a.full_code || a.code || "").localeCompare(
          String(b.full_code || b.code || ""),
          "nl"
        )
      );

      const out = [];

      rows.forEach((x) => {
        const indent = "  ".repeat(depth);

        out.push(
          `${indent}- ${x.full_code || x.code || ""} ${x.name || ""} | laag ${x.level || 1} | prijs ${euro(x.price)}`
        );

        if (x.offer_text) {
          out.push(`${indent}  Offertetekst: ${x.offer_text}`);
        }

        if (x.internal_text) {
          out.push(`${indent}  Interne tekst: ${x.internal_text}`);
        }

        out.push(...walk(x.id, depth + 1));
      });

      return out;
    }

    return [
      `Project: ${form.full_code || form.code || ""} ${form.name || ""}`,
      "",
      "Projectstructuur:",
      ...walk(currentId, 1),
    ].join("\n");
  }

  function appendAiConversation(question, answer) {
    const block =
      `Vraag:\n${question}\n\n` +
      `----------------------------------------\n\n` +
      `Antwoord:\n${answer}\n\n` +
      `========================================\n\n`;

    setAiText((prev) => {
      const cur = String(prev || "").trim();

      if (!cur) return block;

      return `${cur}\n\n${block}`;
    });
  }

  function openAiProject() {
    setAiSource("page");
    setAiOpen(true);
    setAiText("");
    setAiResult("");

    setTimeout(() => {
      try {
        aiTextRef.current?.focus?.({ preventScroll: true });
      } catch {
        try {
          aiTextRef.current?.focus?.();
        } catch {}
      }
    }, 0);
  }

  async function controleerProjectMetAi() {
    setAiBusy(true);
    setAiOpen(true);
    setAiText("AI controleert projectstructuur...");

    try {
      const prompt = `
Je controleert projectbeheer / projectstructuur in StaffIQ.

Controleer:
1. Nummering en hiërarchie
2. Logische opbouw
3. Ontbrekende deelprojecten
4. Duidelijkheid van projectteksten
5. Mogelijke verbeteringen

${buildProjectContext()}

Geef antwoord in duidelijk Nederlands.
      `.trim();

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "chat",
          question: "Controleer projectbeheer",
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt,
        }),
      });

      setAiText(res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      setAiText("FOUT:\n" + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

  async function runAiChat() {
    const question = String(aiText || "").trim();

    if (!question) {
      setAiText("Typ eerst een vraag voor Chat met AI.");
      return;
    }

    setAiBusy(true);

    try {
      const prompt = `
Je bent een assistent voor StaffIQ.

Beantwoord de vraag op basis van dit project:

${buildProjectContext()}

Vraag:
${question}
      `.trim();

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "chat",
          question,
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt,
        }),
      });

      appendAiConversation(question, res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      appendAiConversation(question, "FOUT: " + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

  function sluitAiPopup() {
    setAiOpen(false);
    setAiBusy(false);
  }

async function controleerTekstMetAi() {
    setAiBusy(true);
    setAiOpen(true);
    setAiSource("popup");
    setAiText("AI controleert projectteksten...");

    try {
      const tekst = [
        `Projectonderdeel: ${form.full_code || form.code || ""} ${form.name || ""}`,
        "",
        "Offertetekst:",
        tekstDraft.offer_text || "",
        "",
        "Interne tekst:",
        tekstDraft.internal_text || "",
      ].join("\n");

      const res = await api("/api/ai/organisations/check", {
        method: "POST",
        body: JSON.stringify({
          mode: "check",
          organisation: {
            module: "Projectbeheer",
            code: form.full_code || form.code || "",
            name: form.name || "",
          },
          prompt: `
Controleer deze projecttekst op:
- duidelijkheid
- professionaliteit
- volledigheid
- verbeterpunten

${tekst}
          `.trim(),
        }),
      });

      setAiText(res?.result || "Geen AI-antwoord ontvangen.");
    } catch (e) {
      setAiText("FOUT:\n" + (e?.message || String(e)));
    } finally {
      setAiBusy(false);
    }
  }

function aiButtonContent(label = "AI") {
    return <>✨ {label}</>;
  }

function tekstPopupButtons() {
    const popupButtonBase = isMobile
      ? {
          ...buttonStyle,
          padding: "8px 4px",
          fontSize: 11,
          minWidth: 0,
          width: "100%",
          whiteSpace: "nowrap",
          textAlign: "center",
        }
      : {
          ...buttonStyle,
          minWidth: 0,
          whiteSpace: "nowrap",
        };

    const popupDisabledButton = {
      ...popupButtonBase,
      opacity: 0.5,
      cursor: "not-allowed",
    };

    const popupBlueButton = {
      ...popupButtonBase,
      background: "#2f7fd1",
      color: "white",
      borderColor: "#2f7fd1",
    };

    const popupAiButton = {
      ...popupButtonBase,
      background: "#6f42c1",
      borderColor: "#6f42c1",
      color: "white",
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "0.9fr 0.8fr 1.45fr 1.2fr"
            : "auto auto auto auto",
          gap: isMobile ? 5 : 8,
          justifyContent: isMobile ? "stretch" : "end",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            detectActiveProjectField();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            detectActiveProjectField();
          }}
          onClick={startSpeak}
          disabled={!voice.supported || voice.listening}
          style={
            !voice.supported || voice.listening
              ? popupDisabledButton
              : popupButtonBase
          }
        >
          {voice.listening ? "🎤..." : "Spreek"}
        </button>

        <button
          onClick={controleerTekstMetAi}
          disabled={aiBusy}
          style={aiBusy ? popupDisabledButton : popupAiButton}
        >
          {aiBusy ? "..." : "✨ AI"}
        </button>

        <button
          onClick={bewaarTekstPopup}
          disabled={isReadonly}
          style={isReadonly ? popupDisabledButton : popupBlueButton}
        >
          Opslaan/Sluiten
        </button>

        <button onClick={sluitTekstPopup} style={popupButtonBase}>
          Annuleren
        </button>
      </div>
    );
  }

  const parentOptiesVoorForm = items.filter(
    (x) => String(x.id) !== String(form.id || "")
  );

const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 900;

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    width: "100%",
    padding: "7px 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
    fontSize: 13,
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: 13,
  };

  const disabledButton = {
    ...buttonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
  };

  const blueButton = {
    ...buttonStyle,
    background: "#2f7fd1",
    color: "white",
    borderColor: "#2f7fd1",
  };

  const redButton = {
    ...buttonStyle,
    background: "#b33333",
    color: "white",
    borderColor: "#b33333",
  };

  const th = {
    padding: "6px 8px",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    background: "#fafafa",
    whiteSpace: "nowrap",
    fontSize: 13,
    lineHeight: 1.2,
  };

  const td = {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
    whiteSpace: "nowrap",
    fontSize: 13,
    lineHeight: 1.2,
  };

  const labelStyle = {
    fontSize: 13,
    color: "#333",
    marginBottom: 3,
    display: "block",
  };

return (
    <div ref={pageRef}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>

<div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <h1 style={{ margin: 0 }}>Projectbeheer</h1>
            <span style={{ fontSize: 12, opacity: 0.55 }}>{VERSION}</span>
          </div>

{!isMobile ? (
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <input
                ref={zoekRef}
                value={zoek}
                onChange={(e) => {
                  setZoek(e.target.value);
                  setMode("search");
                  lastActiveFieldRef.current = "zoek";
                }}
                onFocus={() => {
                  setMode("search");
                  lastActiveFieldRef.current = "zoek";
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  detectActiveProjectField();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  detectActiveProjectField();
                }}
                onClick={startSpeak}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? disabledButton : buttonStyle}
                title="1x klikken = 1 commando of invultekst dicteren"
              >
                {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
              </button>

              <button onClick={nieuw} disabled={isReadonly} style={isReadonly ? disabledButton : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuw} style={buttonStyle}>
                🔄 Vernieuw
              </button>

              <button
                onClick={openAiProject}
                disabled={aiBusy || !form.id}
                style={aiBusy || !form.id ? disabledButton : buttonStyle}
                title={!form.id ? "Selecteer eerst een projectregel" : "AI Projectbeheer"}
              >
                {aiButtonContent("AI")}
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={zoekRef}
                  value={zoek}
                  onChange={(e) => {
                    setZoek(e.target.value);
                    setMode("search");
                    lastActiveFieldRef.current = "zoek";
                  }}
                  onFocus={() => {
                    setMode("search");
                    lastActiveFieldRef.current = "zoek";
                  }}
                  placeholder="Zoek..."
                  style={{
                    ...searchInputStyle,
                    width: "100%",
                    minWidth: 0,
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    detectActiveProjectField();
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    detectActiveProjectField();
                  }}
                  onClick={startSpeak}
                  disabled={!voice.supported || voice.listening}
                  style={{
                    ...(!voice.supported || voice.listening ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                  title="1x klikken = 1 commando of invultekst dicteren"
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button
                  onClick={nieuw}
                  disabled={isReadonly}
                  style={{
                    ...(isReadonly ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                >
                  ➕ Nieuw
                </button>

                <button
                  onClick={vernieuw}
                  style={{
                    ...buttonStyle,
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                >
                  🔄 Vernieuw
                </button>

                <button
                  onClick={openAiProject}
                  disabled={aiBusy || !form.id}
                  style={{
                    ...(aiBusy || !form.id ? disabledButton : buttonStyle),
                    padding: "10px 8px",
                    minWidth: 0,
                    fontSize: 13,
                  }}
                  title={!form.id ? "Selecteer eerst een projectregel" : "AI Projectbeheer"}
                >
                  {aiButtonContent("AI")}
                </button>
              </div>
            </>
          )}

          {speechInfo && <div style={{ color: "#555", marginBottom: 8 }}>{speechInfo}</div>}
          {!hideVoiceError && voice.error && (
            <div style={{ color: "crimson", marginBottom: 8 }}>{String(voice.error)}</div>
          )}

          {melding && <div style={{ color: "green", marginBottom: 8 }}>{melding}</div>}
          {fout && <div style={{ color: "crimson", marginBottom: 8 }}>{fout}</div>}

          <div style={{ overflow: "auto", background: "white", borderRadius: 8, border: "1px solid #eee" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: TABLE_MIN_WIDTH }}>
              <thead>
                <tr>
                  <th style={th} onClick={() => sorteer("full_code")}>Nummer{sortMark("full_code")}</th>
                  <th style={th} onClick={() => sorteer("name")}>Naam{sortMark("name")}</th>
                  <th style={th} onClick={() => sorteer("parent")}>Bovenliggend{sortMark("parent")}</th>
                  <th style={th} onClick={() => sorteer("level")}>Laag{sortMark("level")}</th>
                  <th style={th} onClick={() => sorteer("price")}>Prijs{sortMark("price")}</th>
                  <th style={th} onClick={() => sorteer("active")}>Actief{sortMark("active")}</th>
                </tr>

                <tr>
                  <th style={th}>
                    <select value={filterCode} onChange={(e) => setFilterCode(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesCode.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterNaam} onChange={(e) => setFilterNaam(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesNaam.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterParent} onChange={(e) => setFilterParent(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesParent.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}>
                    <select value={filterLaag} onChange={(e) => setFilterLaag(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      {optiesLaag.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </th>

                  <th style={th}></th>

                  <th style={th}>
                    <select value={filterActief} onChange={(e) => setFilterActief(e.target.value)} style={inputStyle}>
                      <option value="">(alle)</option>
                      <option value="Ja">Ja</option>
                      <option value="Nee">Nee</option>
                    </select>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} style={td}>Laden...</td>
                  </tr>
                )}

                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={6} style={td}>Geen projectonderdelen gevonden.</td>
                  </tr>
                )}

                {!loading && sorted.map((x) => (
                  <tr
                    key={x.id}
                    onClick={() => selecteer(x)}
                    style={{
                      background: String(selectedId) === String(x.id) ? "#e7f1ff" : "white",
                      cursor: "pointer",
                    }}
                  >
                    <td style={td}>
                      <span style={{ paddingLeft: Math.max(0, Number(x.level || 1) - 1) * 18, fontWeight: 600 }}>
                        {x.full_code || x.code}
                      </span>
                    </td>
                    <td style={td}>{x.name}</td>
                    <td style={td}>{parentNaam(x.parent_id) || "-"}</td>
                    <td style={td}>{x.level || 1}</td>
                    <td style={{ ...td, textAlign: "right" }}>{euro(x.price)}</td>
                    <td style={td}>{x.is_active === false ? "Nee" : "Ja"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

<div
          ref={rightPanelRef}
          style={{
            background: "#0b4f73",
            borderRadius: 10,
            padding: 10,
            height: isMobile ? "auto" : "calc(100vh - 40px)",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "scroll",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 14,
              boxSizing: "border-box",
              fontSize: 13,
            }}
          >
            <h2 style={{ marginTop: 0 }}>Projectbeheer</h2>

            <div style={{ display: "grid", gap: 10 }}>
              <label>
                <span style={labelStyle}>Bovenliggend onderdeel</span>
<select
                  value={form.parent_id || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "parent";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, parent_id: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="">(geen - hoofdniveau)</option>
                  {parentOptiesVoorForm.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.full_code || p.code || ""} ${p.name || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Nummer/code</span>
                <input
                  ref={codeRef}
                  value={form.code || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "code";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                  placeholder="Bijv. 01"
                />
              </label>

              <label>
                <span style={labelStyle}>Naam</span>
                <input
                  ref={naamRef}
                  value={form.name || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "naam";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                  placeholder="Bijv. Vloer renovatie"
                />
              </label>

<label>
                <span style={labelStyle}>Kopie van</span>

                <select
                  value={form.copy_from_id || ""}
                  onChange={(e) => kopieerVanBron(e.target.value)}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="">(geen kopie)</option>

                  {items.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.full_code || x.code || ""} ${x.name || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: -4,
                  opacity: form.copy_from_id ? 1 : 0.55,
                  cursor:
                    isFormDisabled || !form.copy_from_id
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.copy_children === true}
                  disabled={isFormDisabled || !form.copy_from_id}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      copy_children: e.target.checked,
                    }))
                  }
                />
                <span style={{ fontSize: 13 }}>
                  Onderliggende onderdelen ook kopiëren
                </span>
              </label>

              <div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>Volledig nummer</div>
                <strong>{form.full_code || "(na opslaan)"}</strong>
              </div>

              <div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>Volledige naam</div>
                <strong>{form.full_name || "(na opslaan)"}</strong>
              </div>

              <label>
                <span style={labelStyle}>Regeltype</span>
                <select
                  value={form.line_type || "item"}
                  onChange={(e) => setForm((p) => ({ ...p, line_type: e.target.value }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="item">Artikel</option>
                  <option value="text">Tekst</option>
                  <option value="heading">Kop</option>
                  <option value="subtotal">Subtotaal</option>
                  <option value="total">Totaal</option>
                </select>
              </label>

              <label>
                <span style={labelStyle}>Prijs</span>
                <input
                  ref={prijsRef}
                  value={form.price ?? ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "prijs";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{
                    ...inputStyle,
                    background: !isAmountLineType(form.line_type) ? "#f4f4f4" : inputStyle.background,
                  }}
                  placeholder={isAmountLineType(form.line_type) ? "0.00" : "Niet van toepassing"}
                />
              </label>

              <label>
                <span style={labelStyle}>Btw</span>
                <select
                  value={form.vat_rate || "hoog"}
                  onChange={(e) => setForm((p) => ({ ...p, vat_rate: e.target.value }))}
                  disabled={isFormDisabled || !isAmountLineType(form.line_type)}
                  style={{
                    ...inputStyle,
                    background: !isAmountLineType(form.line_type) ? "#f4f4f4" : inputStyle.background,
                  }}
                >
                  <option value="hoog">21%</option>
                  <option value="laag">9%</option>
                  <option value="0">0%</option>
                  <option value="verlegd">Verlegd</option>
                </select>
              </label>

              <label>
                <span style={labelStyle}>Actief</span>
                <select
                  value={form.is_active === false ? "false" : "true"}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === "true" }))}
                  disabled={isFormDisabled}
                  style={inputStyle}
                >
                  <option value="true">Ja</option>
                  <option value="false">Nee</option>
                </select>
              </label>

              <button
                onClick={openTekstPopup}
                disabled={isFormDisabled}
                style={isFormDisabled ? disabledButton : blueButton}
              >
                Projectteksten bewerken
              </button>

              <label>
                <span style={labelStyle}>Offertetekst</span>
                <textarea
                  ref={offerteTekstRef}
                  value={form.offer_text || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "offer_text";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, offer_text: e.target.value }))}
                  disabled={isFormDisabled}
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical", whiteSpace: "pre-wrap", fontFamily: "Consolas, \"Courier New\", monospace", tabSize: 4 }}
                />
              </label>

              <label>
                <span style={labelStyle}>Interne tekst</span>
                <textarea
                  ref={interneTekstRef}
                  value={form.internal_text || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "internal_text";
                  }}
                  onChange={(e) => setForm((p) => ({ ...p, internal_text: e.target.value }))}
                  disabled={isFormDisabled}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", whiteSpace: "pre-wrap", fontFamily: "Consolas, \"Courier New\", monospace", tabSize: 4 }}
                />
              </label>

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={opslaan} disabled={isFormDisabled} style={isFormDisabled ? disabledButton : blueButton}>
                  💾 Opslaan
                </button>

                <button onClick={verwijderen} disabled={isReadonly || !form.id} style={(isReadonly || !form.id) ? disabledButton : redButton}>
                  🗑 Verwijderen
                </button>
              </div>

              <div style={{ fontSize: 12, opacity: 0.65 }}>
                Projectbeheer-id: {form.id || "(nieuw)"}
              </div>
            </div>
          </div>
        </div>
      </div>

{aiOpen && (
        <div
          onClick={sluitAiPopup}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 10,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: isMobile ? "96vw" : 980,
              height: isMobile ? "88vh" : "90vh",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              padding: 16,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "#fff",
                paddingBottom: 10,
                marginBottom: 12,
                borderBottom: "1px solid #ddd",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <h2 style={{ margin: 0 }}>
                {aiSource === "popup" ? "AI Projectteksten" : "AI Projectbeheer"}
              </h2>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={sluitAiPopup} disabled={aiBusy} style={buttonStyle}>
                  Sluiten
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 12, fontWeight: 700 }}>
              Kies een actie:
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <button
                onClick={controleerProjectMetAi}
                disabled={aiBusy}
                style={aiBusy ? disabledButton : buttonStyle}
              >
                Project controleren
              </button>

              <button
                onClick={runAiChat}
                disabled={aiBusy}
                style={aiBusy ? disabledButton : buttonStyle}
              >
                Chat met AI
              </button>
            </div>

            <div style={{ marginBottom: 8, fontWeight: 700, flex: "0 0 auto" }}>
              AI venster
            </div>

            <textarea
              ref={aiTextRef}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Klik op een AI-keuze, of typ hier je vraag voor Chat met AI."
              style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                height: "100%",
                padding: 12,
                border: "1px solid #ccc",
                borderRadius: 8,
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.45,
                boxSizing: "border-box",
                resize: "none",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>
        </div>
      )}

      {tekstPopupOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
<div
            style={{
              width: isMobile ? "calc(100vw - 28px)" : "min(1000px, 96vw)",
              maxWidth: "100%",
              maxHeight: "92vh",
              overflow: "auto",
              background: "white",
              borderRadius: 10,
              padding: isMobile ? 14 : 18,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              boxSizing: "border-box",
            }}
          >
<div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
                gap: isMobile ? 10 : 12,
                alignItems: "center",
                marginBottom: 12,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <h2 style={{ margin: 0 }}>Projectteksten</h2>
              {tekstPopupButtons()}
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <span style={labelStyle}>Offertetekst</span>
                <textarea
                  ref={popupOfferteTekstRef}
                  value={tekstDraft.offer_text || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "popup_offer_text";
                  }}
                  onChange={(e) =>
                    setTekstDraft((p) => ({
                      ...p,
                      offer_text: e.target.value,
                    }))
                  }
                  disabled={isReadonly}
                  rows={14}
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: 260,
                    whiteSpace: "pre-wrap",
                    fontFamily: 'Consolas, "Courier New", monospace',
                    tabSize: 4,
                  }}
                />
              </label>

              <label>
                <span style={labelStyle}>Interne tekst / werkinstructie</span>
                <textarea
                  ref={popupInterneTekstRef}
                  value={tekstDraft.internal_text || ""}
                  onFocus={() => {
                    lastActiveFieldRef.current = "popup_internal_text";
                  }}
                  onChange={(e) =>
                    setTekstDraft((p) => ({
                      ...p,
                      internal_text: e.target.value,
                    }))
                  }
                  disabled={isReadonly}
                  rows={10}
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: 180,
                    whiteSpace: "pre-wrap",
                    fontFamily: 'Consolas, "Courier New", monospace',
                    tabSize: 4,
                  }}
                />
              </label>

{tekstPopupButtons()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}